import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { CallProvider } from "@/contexts/CallContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { ComparisonBar } from "@/components/ComparisonBar";
import { PropertyComparison } from "@/components/PropertyComparison";
import { GlobalCallHandler } from "@/components/GlobalCallHandler";
import { useComparison } from "@/contexts/ComparisonContext";
import { ConnectionProvider } from "@/hooks/useConnectionStatus";
import { ConnectionBanner } from "@/components/ConnectionBanner";
import { useSupabaseLogger } from "@/hooks/useSupabaseLogger";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { SwipeNavigation } from "@/components/SwipeNavigation";
import { useNativeAppBehaviors } from "@/hooks/useNativeAppBehaviors";

const queryClient = new QueryClient();

function SupabaseLoggerSetup() {
  useSupabaseLogger();
  return null;
}

function NativeAppBehaviors() {
  useNativeAppBehaviors();
  return null;
}

function ComparisonOverlay() {
  const { comparisonList, showComparison, setShowComparison, removeFromComparison } = useComparison();
  
  if (!showComparison || comparisonList.length < 2) return null;
  
  return (
    <PropertyComparison
      properties={comparisonList}
      onRemove={removeFromComparison}
      onClose={() => setShowComparison(false)}
    />
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <LanguageProvider>
        <ConnectionProvider>
          <SupabaseLoggerSetup />
          <AuthProvider>
            <CallProvider>
              <ComparisonProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <ConnectionBanner />
                  <GlobalCallHandler />
                  <BrowserRouter>
                    <NativeAppBehaviors />
                    <SwipeNavigation>
                      <AnimatedRoutes />
                      <CookieConsentBanner />
                      <ComparisonBar />
                      <ComparisonOverlay />
                      <MobileBottomNav />
                    </SwipeNavigation>
                  </BrowserRouter>
                </TooltipProvider>
              </ComparisonProvider>
            </CallProvider>
          </AuthProvider>
        </ConnectionProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
